import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { AlertTriangle, ChevronRight, CalendarClock, PlayCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusDot } from "@/components/ui/status-dot";
import { isDateOverdue } from "@/lib/dateUtils";
import type { InspectionWithProfiles } from "@/hooks/useDashboardQuery";
import {
  type BentoView,
  type GetDisplayStatus,
  inspectionAssetName,
  makeFormatters,
  mobileMppClasses,
  recentPillClasses,
  shortInspectionId,
  statusToDot,
} from "./dashboardBento.helpers";

/**
 * DashboardBentoMobile — Mobile-Stack (Stitch screen-09): Hero-Action-Stack +
 * 2-up-Summary + horizontaler MPP-Scroll + Recent-Cards. Rein praesentational.
 */

interface DashboardBentoMobileProps {
  recentInspections: InspectionWithProfiles[];
  view: BentoView;
  getDisplayStatus: GetDisplayStatus;
}

/** Eine 2-up-Summary-Kachel (Overdue/Due-Today). */
function SummaryTile({
  dot,
  label,
  value,
  valueClass,
}: {
  dot: "fail" | "pending";
  label: string;
  value: number;
  valueClass: string;
}) {
  return (
    <div className="bg-surface-container-lowest border border-outline-variant p-4 flex flex-col gap-2 rounded">
      <div className="flex items-center gap-2">
        <StatusDot state={dot} />
        <span className="text-label-caps text-muted-foreground">{label}</span>
      </div>
      <span className={cn("font-display text-[2rem] font-bold leading-none", valueClass)}>
        {value}
      </span>
    </div>
  );
}

/** Eine horizontale MPP-Scroll-Karte (urgent = ueberfaellig). Rein praesentational. */
function MobileMppCard({
  inspection,
  onOpen,
  dueChip,
}: {
  inspection: InspectionWithProfiles;
  onOpen: () => void;
  dueChip: (iso: string | null) => string;
}) {
  const { t } = useTranslation();
  const urgent = isDateOverdue(inspection.due_date);
  const classes = mobileMppClasses(urgent);
  const badgeLabel = urgent ? t("dashboard.bento.mppUrgent") : t("dashboard.bento.mppTitle");
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "min-w-[280px] text-left bg-surface-container border border-outline-variant p-4 snap-center rounded relative overflow-hidden active:scale-95 transition-transform",
        classes.container,
      )}
    >
      <span
        className={cn(
          "inline-block text-[10px] font-bold px-2 py-0.5 mb-3 rounded-sm",
          classes.badge,
        )}
      >
        {badgeLabel}
      </span>
      <h4 className="font-bold text-base text-foreground mb-1 truncate">
        {inspectionAssetName(inspection, t("dashboard.unknownChecklist"))}
      </h4>
      {inspection.checklists?.name && (
        <p className="text-sm text-muted-foreground mb-4 truncate">{inspection.checklists.name}</p>
      )}
      <div className="flex items-center gap-2 text-muted-foreground">
        <CalendarClock className="h-4 w-4" />
        <span className="text-data-tabular">
          {t("dashboard.bento.mppDue")}: {dueChip(inspection.due_date)}
        </span>
      </div>
    </button>
  );
}

export function DashboardBentoMobile({
  recentInspections,
  view,
  getDisplayStatus,
}: DashboardBentoMobileProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatTimestamp, formatDueChip } = makeFormatters(i18n.language);
  const dueChip = (iso: string | null) => formatDueChip(iso, t("dashboard.bento.mppNoDueDate"));

  return (
    <div className="md:hidden space-y-8" data-testid="dashboard-bento-mobile">
      {/* Hero Action Stack */}
      <section className="flex flex-col gap-1">
        <button
          type="button"
          onClick={() => navigate("/inspections")}
          className="bg-secondary text-secondary-foreground h-14 flex items-center justify-center gap-2 font-bold rounded-lg shadow-sm active:scale-95 transition-transform"
        >
          <PlayCircle className="h-5 w-5" />
          <span>{t("inspections.start")}</span>
        </button>
        {/* TES-1733 (N3): kanonische „Mangel erfassen"-Variante = Safety-Yellow (action/bg-secondary),
            konsistent mit Dashboard-Desktop, Mängel-Liste und dem vereinheitlichten Dialog (TES-1722). */}
        <button
          type="button"
          onClick={() => navigate("/defects")}
          className="bg-secondary text-secondary-foreground h-12 flex items-center justify-center gap-2 font-bold rounded-lg shadow-sm active:scale-95 transition-transform"
        >
          <AlertTriangle className="h-5 w-5" />
          <span>{t("defects.create")}</span>
        </button>
      </section>

      {/* 2-up Summary */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-primary">
            {t("dashboard.bento.openDefects")}
          </h3>
          <span className="text-label-caps text-muted-foreground">
            {t("dashboard.bento.summaryRealtime")}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <SummaryTile
            dot="fail"
            label={t("dashboard.bento.summaryOverdue")}
            value={view.overdueDefects}
            valueClass="text-destructive"
          />
          <SummaryTile
            dot="pending"
            label={t("dashboard.bento.summaryDueToday")}
            value={view.dueTodayDefects}
            valueClass="text-secondary-foreground"
          />
        </div>
      </section>

      {/* Horizontal MPP Scroll */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-primary">
            {t("dashboard.bento.mppTitle")}
          </h3>
          <Link to="/schedules" className="text-primary text-sm font-bold">
            {t("common.all")}
          </Link>
        </div>
        {view.mppInspections.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dashboard.bento.mppEmpty")}</p>
        ) : (
          <div className="flex overflow-x-auto gap-4 pb-4 -mx-4 px-4 snap-x">
            {view.mppInspections.map((inspection) => (
              <MobileMppCard
                key={inspection.id}
                inspection={inspection}
                onOpen={() => navigate(`/inspections/${inspection.id}`)}
                dueChip={dueChip}
              />
            ))}
          </div>
        )}
      </section>

      {/* Recent Inspections Cards */}
      <section>
        <h3 className="font-display text-lg font-semibold text-primary mb-4">
          {t("dashboard.bento.recentInspections")}
        </h3>
        {recentInspections.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dashboard.noPendingInspections")}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {recentInspections.slice(0, 5).map((inspection) => {
              const displayStatus = getDisplayStatus(inspection);
              const { resultKey } = statusToDot(displayStatus);
              return (
                <button
                  key={inspection.id}
                  type="button"
                  onClick={() => navigate(`/inspections/${inspection.id}`)}
                  className="text-left bg-surface-container-lowest border border-outline-variant p-4 rounded hover:bg-surface-container-high transition-colors"
                >
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <div className="min-w-0">
                      <span className="text-label-caps text-muted-foreground">
                        {shortInspectionId(inspection.id)}
                      </span>
                      <h4 className="font-bold text-base truncate">
                        {inspectionAssetName(inspection, t("dashboard.unknownChecklist"))}
                      </h4>
                    </div>
                    <span
                      className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase shrink-0",
                        recentPillClasses(displayStatus),
                      )}
                    >
                      {t(`dashboard.bento.${resultKey}`)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-muted-foreground">
                    <span className="text-data-tabular">
                      {formatTimestamp(inspection.created_at)}
                    </span>
                    <span className="flex items-center text-primary font-bold">
                      {t("common.view")}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
